import { AsyncPipe, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { Observable } from 'rxjs';
import { LoadingSpinnerComponent } from '../../loading-spinner/loading-spinner.component';
import { QuizService, QuizState } from '../data-access/quiz.service';
import { CodecraftQuizErrorPageComponent } from '../ui/error-page.component';
import { CodecraftQuizCompletedPageComponent } from './completed-page.component';
import { CodecraftQuizDetailPageComponent } from './detail-page.component';
import { CodecraftQuizQuestionPageWrapperComponent } from './question-page-wrapper.component';

@Component({
  standalone: true,
  selector: 'codecraft-quiz',
  template: `
    <div *ngIf="uid && quizState$ | async as quizState; else loading">
      <div [ngSwitch]="quizState.component">
        <codecraft-quiz-detail-page
          *ngSwitchCase="'not-started'"
        ></codecraft-quiz-detail-page>
        <codecraft-quiz-detail-page
          *ngSwitchCase="'resuming'"
        ></codecraft-quiz-detail-page>
        <codecraft-quiz-question-page-wrapper
          *ngSwitchCase="'in-progress'"
        ></codecraft-quiz-question-page-wrapper>
        <codecraft-quiz-completed-page
          *ngSwitchCase="'completed'"
        ></codecraft-quiz-completed-page>
        <codecraft-quiz-error-page
          *ngSwitchCase="'error'"
          [error]="quizState.error"
        ></codecraft-quiz-error-page>
        <codecraft-quiz-error-page
          *ngSwitchDefault
          [error]="'Invalid quiz state'"
        ></codecraft-quiz-error-page>
      </div>
    </div>
    <ng-template #loading>
      <p class="h1">Quiz Loading</p>
      <hr />
      <codecraft-loading-spinner></codecraft-loading-spinner>
    </ng-template>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
  imports: [
    NgIf,
    AsyncPipe,
    NgSwitch,
    NgSwitchCase,
    CodecraftQuizDetailPageComponent,
    CodecraftQuizQuestionPageWrapperComponent,
    CodecraftQuizCompletedPageComponent,
    CodecraftQuizErrorPageComponent,
    LoadingSpinnerComponent,
  ],
})
export class CodecraftQuizComponent implements OnInit {
  @Input() quizid!: string;
  @Input() uid!: string;

  quizState$!: Observable<QuizState>;
  constructor(private quizService: QuizService) {}

  async ngOnInit(): Promise<void> {
    // Get token from cookie named `session`
    const idToken = document.cookie
      ?.split('; ')
      ?.find((row) => row.startsWith('session='))
      ?.split('=')[1];

    if (idToken) {
      try {
        const decoded: any = jwtDecode(idToken);
        this.uid = decoded.user_id || decoded.uid;
      } catch (e) {
        this.quizService.setQuizState({
          component: 'error',
          error: 'Invalid JWT token',
        });
        return;
      }
    }

    if (!this.uid) {
      this.quizService.setQuizState({
        component: 'error',
        error: 'Missing uid',
      });
      return;
    }
    if (!this.quizid) {
      this.quizService.setQuizState({
        component: 'error',
        error: 'Missing quizid',
      });
      return;
    }
    try {
      await this.quizService.initialize(this.uid, this.quizid);
      this.quizState$ = this.quizService.getQuizState$();
    } catch (error) {
      console.error('Init error:', error);
      this.quizService.setQuizState({
        component: 'error',
        error: 'Quiz component init error: ' + error,
      });
    }
  }
}
